import axios from '@/libs/axios'

const PreReviewService = {
  /**
   * Create a pre-review task for creatives
   * @param {Object} params - Pre-review parameters
   * @param {string} params.advertiser_id - Advertiser ID
   * @param {Array} params.materials - Array of materials to review
   * @param {string} params.location - Location code (e.g., "US")
   * @param {boolean} params.e_commerce - Is ecommerce ad
   * @returns {Promise} Axios response
   */
  createTask(params) {
    return axios.post('/api/pre-review/create', {
      advertiser_id: params.advertiser_id,
      materials: params.materials,
      location: params.location || 'US',
      e_commerce: params.e_commerce || false,
    })
  },

  /**
   * Get status of a pre-review task
   * @param {Object} params - Parameters
   * @param {string} params.task_id - Task ID
   * @param {string} params.advertiser_id - Advertiser ID
   * @returns {Promise} Axios response
   */
  getTaskStatus(params) {
    return axios.get(`/api/pre-review/status/${params.task_id}`, {
      params: {
        advertiser_id: params.advertiser_id,
      },
    })
  },

  /**
   * Get history of pre-review tasks
   * @param {Object} params - Parameters
   * @param {string} params.advertiser_id - Advertiser ID
   * @param {number} params.page - Page number (default: 1)
   * @param {number} params.size - Page size (default: 10)
   * @param {string} params.status - Filter by status (optional)
   * @returns {Promise} Axios response
   */
  getTaskHistory(params) {
    return axios.get('/api/pre-review/history', {
      params: {
        advertiser_id: params.advertiser_id,
        page: params.page || 1,
        size: params.size || 10,
        status: params.status,
      },
    })
  },

  /**
   * Submit creatives for pre-review
   * @param {Object} params - Parameters
   * @param {string} params.advertiser_id - Advertiser ID
   * @param {Array} params.creatives - Array of creative objects to review
   * @param {string} params.ad_text - Ad text content (optional)
   * @param {string} params.landing_page_url - Landing page URL (optional)
   * @param {string} params.location - Location code (default: "US")
   * @param {boolean} params.is_ecommerce - Is ecommerce ad (default: false)
   * @returns {Promise} Axios response
   */
  submitCreativesForReview(params) {
    const materials = []

    // Add creative materials (images/videos)
    if (params.creatives && params.creatives.length > 0) {
      params.creatives.forEach(creative => {
        // Use TikTok material ID from metadata, fallback to our asset ID
        const materialId = String(creative.metadata?.tiktok_material_id || creative.id)

        const material = {
          type: 'IMAGE', // Changed from material_type to type
          content: materialId, // Changed from material_id to content
        }

        materials.push(material)
      })
    }

    // Add ad text if provided
    if (params.ad_text && params.ad_text.trim()) {
      materials.push({
        type: 'AD_TEXT', // Changed from material_type to type
        content: params.ad_text.trim(), // Changed from text to content
      })
    }

    // Add landing page URL if provided
    if (params.landing_page_url && params.landing_page_url.trim()) {
      materials.push({
        type: 'LANDING_PAGE_URL', // Changed from material_type to type
        content: params.landing_page_url.trim(), // Changed from landing_page_url to content
      })
    }

    const taskParams = {
      advertiser_id: params.advertiser_id,
      materials,
      location: params.location || 'US',
      e_commerce: params.is_ecommerce || false,
    }

    return this.createTask(taskParams)
  },
}

export default PreReviewService
